// /components/Bidding/useSubscriptions.js
import { useContext } from 'react';
import { useSubscription } from '@apollo/client';
import isEqual from 'react-fast-compare';

import AuthContext from 'src/contexts/AuthContext';

import {
  BIDDING_SALE_ASSETS,
  BIDDING_SALE_ASSET_UPDATED,
  BIDDING_UPDATED,
  SALE,
  SALE_UPDATED,
} from './queries';

/**
 * Safely extract a remaining-time value in milliseconds from various possible
 * subscription field names. If not present, returns undefined.
 */
const extractCountdownMs = (updates) => {
  // Preferred (ms)
  if (typeof updates?.countdownTimeMs === 'number') return updates.countdownTimeMs;
  if (typeof updates?.countdown_time_ms === 'number') return updates.countdown_time_ms;

  // Seconds-based fallbacks
  if (typeof updates?.countdownTime === 'number') return updates.countdownTime * 1000;
  if (typeof updates?.countdown_time === 'number') return updates.countdown_time * 1000;

  // Some schemas send only a server "current time"; not enough to compute remaining
  return undefined;
};

const useSubscriptions = ({ id }) => {
  const { apolloClient: client } = useContext(AuthContext);

  // --- SaleUpdated: replace the whole sale object snapshot ---
  useSubscription(SALE_UPDATED, {
    variables: { id },
    onData: ({ data: { data } }) => {
      console.log('[SALE_UPDATED]', data);
      if (!data?.saleUpdated?.sale) return;

      client.writeQuery({
        query: SALE,
        variables: { id },
        data: { sale: data.saleUpdated.sale },
      });
    },
  });

  // --- BiddingUpdated: fine-grained live updates (bids, flash, status, timers) ---
  useSubscription(BIDDING_UPDATED, {
    variables: { saleId: id },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      console.log('[BIDDING_UPDATED]', data);
      const updates = data?.biddingUpdated;
      if (!updates) return;

      const cached = client.readQuery({ query: SALE, variables: { id } });
      const sale = cached?.sale;
      if (!sale) return;

      // Drop stale updates using server-issued millisecond timestamp.
      if (updates.timestamp && sale.biddingUpdatedTimestamp) {
        if (updates.timestamp < sale.biddingUpdatedTimestamp) return;
      }

      const updatedSale = { ...sale };

      // Update currently bidding sale asset if this payload targets it.
      const biddingSaleAsset = sale.biddingSaleAsset;
      if (biddingSaleAsset && updates.saleAssetId === biddingSaleAsset.id) {
        const updatedSaleAsset = { ...biddingSaleAsset };

        if (updates.biddingNextUpdated) {
          if (typeof updates.biddingNext === 'number') {
            updatedSaleAsset.biddingNext = updates.biddingNext;
          }
          if (typeof updates.biddingNextManual === 'number' || updates.biddingNextManual === null) {
            updatedSaleAsset.biddingNextManual = updates.biddingNextManual;
          }
          if (typeof updates.biddingStep === 'number') {
            updatedSaleAsset.biddingStep = updates.biddingStep;
          }
        }

        if (updates.bidsUpdated) {
          if (Array.isArray(updates.bids)) updatedSaleAsset.bids = updates.bids;
          if (updates.nextRemovedBid) updatedSaleAsset.nextRemovedBid = updates.nextRemovedBid;
          if (typeof updates.highBid !== 'undefined') updatedSaleAsset.highBid = updates.highBid;
        }

        if (updates.biddingStatusUpdated && typeof updates.biddingStatus === 'string') {
          updatedSaleAsset.biddingStatus = updates.biddingStatus;
        }

        // Optional per-lot countdown remaining time (ms), if provided by the server.
        const countdownMs = extractCountdownMs(updates);
        if (typeof countdownMs === 'number') {
          updatedSaleAsset.countdownTimeMs = countdownMs;
        }

        updatedSale.biddingSaleAsset = updatedSaleAsset;
      }

      // Flash message & colors (sale-level)
      if (updates.biddingFlashUpdated) {
        updatedSale.biddingFlash = updates.biddingFlash;
        updatedSale.biddingFlashBackgroundColor = updates.biddingFlashBackgroundColor;
        updatedSale.biddingFlashForegroundColor = updates.biddingFlashForegroundColor;
        updatedSale.biddingBuyerBackgroundColor = updates.biddingBuyerBackgroundColor;
        updatedSale.biddingBuyerForegroundColor = updates.biddingBuyerForegroundColor;
        updatedSale.biddingFlashFades = updates.biddingFlashFades;
        updatedSale.biddingFlashType = updates.biddingFlashType;
      }

      // Bidding notice (sale-level)
      if (updates.biddingNoticeUpdated) {
        updatedSale.biddingNotice = updates.biddingNotice;
      }

      // Active audio/video flag (sale-level)
      if (updates.audioStreamUpdated) {
        updatedSale.activeStream = updates.activeStream;
      }

      // Server timestamp for dedupe + client sync
      if (updates.timestamp) {
        updatedSale.biddingUpdatedTimestamp = updates.timestamp;
      }

      client.writeQuery({
        query: SALE,
        variables: { id },
        data: { sale: updatedSale },
      });
    },
  });

  // --- SaleAssetUpdated: snapshot updates for any sale asset in the list ---
  useSubscription(BIDDING_SALE_ASSET_UPDATED, {
    variables: { saleId: id },
    onData: ({ data: { data } }) => {
      console.log('[BIDDING_SALE_ASSET_UPDATED]', data);
      const updatedSaleAsset = data?.saleAssetUpdated?.saleAsset;
      if (!updatedSaleAsset) return;

      const cached = client.readQuery({
        query: BIDDING_SALE_ASSETS,
        variables: { saleId: id },
      });

      const saleAssets = cached?.saleAssets;
      if (!Array.isArray(saleAssets)) return;

      const updatedIndex = saleAssets.findIndex((sa) => sa.id === updatedSaleAsset.id);

      // If we already have this index and the entry is deeply equal, skip.
      if (updatedIndex > -1 && isEqual(saleAssets[updatedIndex], updatedSaleAsset)) {
        return;
      }

      // Remove any existing copy of this asset
      let next = saleAssets.filter((sa) => sa.id !== updatedSaleAsset.id);

      // If it's not a tombstone/destroyed, place it back—prefer preserving position.
      if (!updatedSaleAsset.destroyed) {
        if (updatedIndex > -1) {
          next.splice(updatedIndex, 0, updatedSaleAsset);
        } else {
          next = [...next, updatedSaleAsset];
        }
      }

      client.writeQuery({
        query: BIDDING_SALE_ASSETS,
        variables: { saleId: id },
        data: { saleAssets: next },
      });
    },
  });
};

export default useSubscriptions;
